import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { baseUrl } from "../../../services/config";
import { PremissionChecker } from "../../../services/PremissionChecker";

function CRMActivities({ inquiry_id, getInquiries, closeCRMModal }) {
  const emptyActivity = {
    inquiry_id: inquiry_id || "",
    activity_type_id: "",
    activity_date: "",
    remarks: "",
    next_followup_date: "",
  };

  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [activities, setActivities] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [form, setForm] = useState(emptyActivity);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach((item) => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert],
  );

  const getActivities = useCallback(
    async (inquiry_id) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}/crm-activities/inquiry/${inquiry_id}`,
          {
            headers: { Authorization: `Bearer ${authtoken}` },
          },
        );
        const data = await response.json();
        if (!response.ok || data?.metadata?.success === false) {
          showErrors(data, "Unable to load CRM activities");
          return;
        }
        setActivities(data?.payload || []);
      } catch (error) {
        alert.error("Unable to load CRM activities");
      } finally {
        setLoading(false);
      }
    },
    [alert, authtoken, showErrors],
  );

  const getOptions = useCallback(async () => {
    const headers = { Authorization: `Bearer ${authtoken}` };
    try {
      const [inquiryResponse, activityTypeResponse] = await Promise.all([
        fetch(`${baseUrl}/crm-inquiries`, { headers }),
        fetch(`${baseUrl}/crm-activity-types`, { headers }),
      ]);
      const [inquiryData, activityTypeData] = await Promise.all([
        inquiryResponse.json(),
        activityTypeResponse.json(),
      ]);
      if (inquiryResponse.ok && inquiryData?.metadata?.success !== false) {
        setInquiries(inquiryData?.payload || []);
      }
      if (
        activityTypeResponse.ok &&
        activityTypeData?.metadata?.success !== false
      ) {
        setActivityTypes(activityTypeData?.payload || []);
      }
    } catch (error) {
      alert.error("Unable to load CRM activity form options");
    }
  }, [alert, authtoken]);

  useEffect(() => {
    inquiry_id && getActivities(inquiry_id);
    getOptions();
  }, [getActivities, getOptions, inquiry_id]);

  const inquiryOptions = useMemo(
    () =>
      inquiries.map((inquiry) => ({
        value: inquiry.id,
        label: `${inquiry.inquiry_no ||
          `Inquiry #${inquiry.id}`} - ${inquiry.student_name ||
          "Unknown student"}`,
      })),
    [inquiries],
  );

  const inquiryName = (inquiryId) =>
    inquiryOptions.find((option) => String(option.value) === String(inquiryId))
      ?.label || inquiryId;

  const activityTypeName = (typeId) =>
    activityTypes.find((type) => String(type.id) === String(typeId))?.name ||
    typeId;

  const closeModal = () => {
    setModal(null);
    setSelectedActivity(null);
    setForm(emptyActivity);
  };

  const openEditModal = (activity) => {
    setSelectedActivity(activity);
    setForm({
      inquiry_id: activity.inquiry_id ?? "",
      activity_type_id: activity.activity_type_id ?? "",
      activity_date: activity.activity_date ?? null,
      remarks: activity.remarks ?? "",
      next_followup_date: activity.next_followup_date ?? null,
    });
    setModal("edit");
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const saveActivity = async () => {
    if (!form.inquiry_id) {
      alert.error("Inquiry is required");
      return;
    }
    if (!form.activity_type_id) {
      alert.error("Activity Type is required");
      return;
    }
    if (!form.activity_date.trim()) {
      alert.error("Activity Date is required");
      return;
    }

    const isEditing = modal === "edit";
    const payload = {
      inquiry_id: Number(form.inquiry_id),
      activity_type_id: Number(form.activity_type_id),
      activity_date: form.activity_date || null,
      remarks: form.remarks.trim() || null,
      next_followup_date: form.next_followup_date || null,
    };

    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-activities${
          isEditing ? `/${selectedActivity.id}` : ""
        }`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, `Unable to ${isEditing ? "update" : "add"} activity`);
        return;
      }
      alert.success(
        `CRM activity successfully ${isEditing ? "updated" : "added"}`,
      );
      closeModal();
      getInquiries(false);
      closeCRMModal();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} activity`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteActivity = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-activities/${selectedActivity.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` },
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete CRM activity");
        return;
      }
      alert.success(data?.payload || "CRM activity successfully deleted");
      closeModal();
      getInquiries(false);
      closeCRMModal();
    } catch (error) {
      alert.error("Unable to delete CRM activity");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedInquiry =
    inquiryOptions.find(
      (option) => String(option.value) === String(form.inquiry_id),
    ) || null;

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_activity.create") && (
        <button
          className="btn btn-primary mr-2"
          onClick={() => {
            setForm(emptyActivity);
            setModal("add");
          }}
        >
          Add CRM Activity
        </button>
      )}

      <Table className="mt-5" responsive>
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Activity Type</th>
            <th>Remarks</th>
            <th>Activity Date</th>
            <th>Next Follow-up Date</th>
            {PremissionChecker(user, "crm_activity.update") && <th>Update</th>}
            {PremissionChecker(user, "crm_activity.delete") && <th>Delete</th>}
          </tr>
        </thead>
        <tbody>
          {activities.map((activity) => (
            <tr className="text-center" key={activity.id}>
              <td>{activity.id}</td>
              <td>{activity.activity_type?.name}</td>
              <td>{activity.remarks}</td>
              <td>{activity.activity_date || "-"}</td>
              <td>{activity.next_followup_date || "-"}</td>
              {PremissionChecker(user, "crm_activity.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => openEditModal(activity)}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_activity.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedActivity(activity);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && activities.length === 0 && (
        <div className="text-center py-4">No CRM activities found.</div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="lg"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Activity
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-4">
              <label className="form-control-label">
                <b>
                  Inquiry <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <Select
                isDisabled
                value={selectedInquiry}
                options={inquiryOptions}
                onChange={(option) =>
                  setForm((current) => ({
                    ...current,
                    inquiry_id: option ? option.value : "",
                  }))
                }
                placeholder="Search inquiry by number or student name"
                isClearable
                isSearchable
              />
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>
                  Activity Type <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <select
                className="form-control form-select"
                name="activity_type_id"
                value={form.activity_type_id}
                onChange={updateField}
              >
                <option value="">Select Activity Type</option>
                {activityTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>
                  Activity Date <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <input
                type="date"
                className="form-control"
                name="activity_date"
                value={form.activity_date}
                onChange={updateField}
              />
            </div>

            <div className="col-md-12 mb-4">
              <label className="form-control-label">
                <b>Remarks</b>
              </label>
              <textarea
                className="form-control"
                name="remarks"
                placeholder="Enter remarks..."
                value={form.remarks}
                onChange={updateField}
                rows="3"
              />
            </div>
            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>Next Follow-up Date</b>
              </label>
              <input
                type="date"
                className="form-control"
                name="next_followup_date"
                value={form.next_followup_date}
                onChange={updateField}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn btn-primary w-100px" onClick={saveActivity}>
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM activity?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteActivity}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default CRMActivities;
